/**
 * PublicCourseDetailPage — public course detail page for a standalone course
 * (Round 40 · Task 8). Anonymous visitors can browse; the "Enrol" CTA routes
 * signed-in students to the shared course-enrolment flow. Free courses land
 * straight in the student's LMS after signup; paid courses go through the
 * existing bank-transfer + admin approval workflow.
 */
import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import PublicNav from "../components/PublicNav";
import PublicFooter from "../components/PublicFooter";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { GraduationCap, Clock, Users, CurrencyNgn, ArrowRight, BookOpen, Target, Trophy } from "@phosphor-icons/react";

export default function PublicCourseDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/courses/public/${id}`)
      .then((r) => setCourse(r.data))
      .catch(() => setCourse(null))
      .finally(() => setLoading(false));
  }, [id]);

  const enrol = async () => {
    if (!user) {
      navigate(`/register?redirect=/courses/public/${id}`);
      return;
    }
    setEnrolling(true);
    setError("");
    try {
      // Reuse the standard course enrolment endpoint; the backend already
      // supports course_id-based enrolments alongside programme_id ones.
      await api.post("/enrollments/course", { course_id: id });
      navigate(course?.is_free ? `/courses/${id}` : `/payment?course=${id}`);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not start enrolment. Please try again.");
    }
    setEnrolling(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><p className="text-sm text-slate-500">Loading course…</p></div>;
  if (!course) {
    return (
      <div className="min-h-screen bg-slate-50">
        <PublicNav />
        <div className="max-w-3xl mx-auto p-10 text-center">
          <p className="text-slate-500">Course not found.</p>
          <Link to="/courses" className="text-blue-800 underline text-sm mt-3 inline-block">Back to catalogue</Link>
        </div>
        <PublicFooter />
      </div>
    );
  }

  const objectives = (course.objectives || "").split(/\r?\n/).filter(Boolean);
  const outcomes = (course.outcomes || course.learning_outcomes || "").split(/\r?\n/).filter(Boolean);
  const faqs = Array.isArray(course.faqs) ? course.faqs : [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-course-detail">
      <PublicNav />

      <section className="bg-slate-950 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-14 grid md:grid-cols-3 gap-10">
          <div className="md:col-span-2">
            <p className="uppercase tracking-widest text-[10px] text-amber-400 font-bold mb-3">Standalone course · {course.code || ""}</p>
            <h1 className="text-3xl md:text-5xl font-bold" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="course-title">{course.title}</h1>
            <p className="mt-4 text-sm md:text-base text-slate-300 max-w-2xl whitespace-pre-wrap">{course.description}</p>
            <div className="mt-6 flex flex-wrap gap-3 text-sm text-slate-300">
              {course.duration && <span className="inline-flex items-center gap-1"><Clock size={14} /> {course.duration}</span>}
              <span className="inline-flex items-center gap-1"><Users size={14} /> {course.enrollee_count || 0} enrolled</span>
              <span className="inline-flex items-center gap-1"><BookOpen size={14} /> {course.lesson_count || 0} lessons</span>
            </div>
          </div>
          <aside className="bg-white text-slate-900 p-5 self-start" data-testid="course-enrol-card">
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Enrolment</p>
            {course.is_free ? (
              <p className="text-2xl font-bold text-green-700 mb-3">Free</p>
            ) : course.fee ? (
              <p className="text-2xl font-bold text-slate-950 mb-3 inline-flex items-center gap-1">
                <CurrencyNgn size={20} weight="bold" /> {Number(course.fee).toLocaleString()}
              </p>
            ) : (
              <p className="text-lg text-slate-500 mb-3">Contact admissions</p>
            )}
            {course.is_free && <p className="text-[11px] text-green-700 mb-3">Badge on completion · no transcript / programme certificate.</p>}
            <button
              onClick={enrol}
              disabled={enrolling}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-blue-800 hover:bg-blue-900 text-white text-sm rounded-none disabled:opacity-50"
              data-testid="course-enrol-btn"
            >
              {enrolling ? "Enrolling…" : (course.is_free ? "Enrol for free" : "Enrol now")} <ArrowRight size={14} weight="bold" />
            </button>
            {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
          </aside>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-12 space-y-12">
        {objectives.length > 0 && (
          <section data-testid="course-objectives">
            <h2 className="text-xl font-bold text-slate-950 mb-3 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <Target size={20} className="text-blue-800" /> Learning objectives
            </h2>
            <ul className="space-y-2 list-disc list-inside text-slate-800">
              {objectives.map((o, i) => <li key={i}>{o}</li>)}
            </ul>
          </section>
        )}

        {outcomes.length > 0 && (
          <section data-testid="course-outcomes">
            <h2 className="text-xl font-bold text-slate-950 mb-3 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <Trophy size={20} className="text-amber-500" /> Learning outcomes
            </h2>
            <ul className="space-y-2 list-disc list-inside text-slate-800">
              {outcomes.map((o, i) => <li key={i}>{o}</li>)}
            </ul>
          </section>
        )}

        {(course.instructor_name || course.instructor_bio) && (
          <section data-testid="course-instructor">
            <h2 className="text-xl font-bold text-slate-950 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Instructor</h2>
            <p className="text-slate-900 font-semibold">{course.instructor_name}</p>
            {course.instructor_bio && <p className="text-slate-700 mt-1 whitespace-pre-wrap">{course.instructor_bio}</p>}
          </section>
        )}

        {faqs.length > 0 && (
          <section data-testid="course-faqs">
            <h2 className="text-xl font-bold text-slate-950 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Frequently asked questions</h2>
            <div className="space-y-3">
              {faqs.map((f, i) => (
                <details key={i} className="border border-slate-200 bg-white p-4">
                  <summary className="cursor-pointer text-sm font-semibold text-slate-900">{f.question || f.q}</summary>
                  <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{f.answer || f.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}
      </div>

      <PublicFooter />
    </div>
  );
}
